/**
 * The body a medication create request carries.
 *
 * Kept apart from the hooks that send it so the one thing here that is easy to
 * get wrong — which of `identity`, `medicationId` and `rawMedicationName` go
 * on the wire — can be asserted directly, without pulling React Native into a
 * test for a question that is only about JSON.
 */
import type { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";

import type { MedicationIdentityInput } from "@src/packages/medications";
import type { PatientMedicationRoute } from "./medication-route";
import type { buildMedicationReminderPayload } from "./medication-reminder-payload";

/** API enum for a patient medication's delivery form, derived from the schema. */
type PatientMedicationFormValue = NonNullable<
  NonNullable<
    paths["/{tenantId}/patients/{patientId}/medications/{medicationId}"]["patch"]["requestBody"]
  >["content"]["application/json"]["form"]
>;

/**
 * The request body the medications create endpoint accepts.
 *
 * `source` is not in the generated contract and never has been, but this app
 * has sent it since the endpoint existed. It is kept — and named here rather
 * than buried in an inline literal — because dropping it is a behaviour change
 * for the server to decide, not a typing convenience.
 */
export type CreateMedicationRequestBody = RequestBody<
  paths["/{tenantId}/patients/{patientId}/medications"]["post"]
> & { source?: "patient" };

export interface CreateMedicationPayload {
  name: string;
  startDate: string;
  endDate?: string | null;
  dose?: number | null;
  doseUnit?: string | null;
  timeTaken?: string[];
  notes?: string | null;
  /** Administration route (e.g., "subcutaneous", "oral") */
  route?: PatientMedicationRoute | null;
  /** Delivery form for injectable meds (e.g., "pre_filled_pen", "injection_solution") */
  form?: string | null;
  /** Optional: Link medication to a treatment cycle */
  cycleId?: string | null;
  /** Optional: Link medication to a specific treatment phase (e.g., "pre", "stim") */
  phaseId?: string | null;
  /** Optional: Link to a specific medication catalog entry */
  medicationId?: string | null;
  /**
   * Patient-facing display override, persisted server-side (#319). Catalog
   * names are never touched; readers fall back to medication.name, then
   * rawMedicationName.
   */
  displayName?: string | null;
  /**
   * Medication role. The API validates this against its own list now
   * (dashboard-api#1904), so the type comes from the contract rather than
   * being restated here; cross-surface trigger behavior still derives from
   * phase.triggerShot.
   */
  medicationRole?: CreateMedicationRequestBody["medicationRole"];
  /**
   * What the patient said the medication is — country, product, dose form,
   * strength, container, pack size — plus how she said it (a scan, a photo,
   * or by typing). The server resolves these to the exact pack and derives the
   * raw medication name from them, so a request carrying an identity sends
   * neither `rawMedicationName` nor a `medication` object
   * (dashboard-api#1904).
   */
  identity?: MedicationIdentityInput;
}

function isUUID(v: string | null | undefined): v is string {
  // The pattern is declared inside the function on purpose. Calling a method
  // on a module-level object is, to the visual-coverage tracer,
  // indistinguishable from writing to shared state, so a module-level regex
  // made every change anywhere in its file trace to every screen importing
  // anything from it.
  const uuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return typeof v === "string" && uuid.test(v);
}

export function buildCreateMedicationBody(
  payload: CreateMedicationPayload,
  reminder?: ReturnType<typeof buildMedicationReminderPayload>,
): CreateMedicationRequestBody {
  return {
    startDate: payload.startDate,
    endDate: payload.endDate,
    dose: payload.dose,
    doseUnit: payload.doseUnit,
    timeTaken: payload.timeTaken as string[] | null | undefined,
    route: payload.route,
    notes: payload.notes,
    source: "patient" as const,
    ...(isUUID(payload.cycleId) && { cycleId: payload.cycleId }),
    ...(isUUID(payload.phaseId) && { phaseId: payload.phaseId }),
    // An identity names the medication on its own: the server stores what the
    // patient reported, resolves the pack, and derives the raw name from
    // `identity.productName`. Sending a catalog id or a raw name alongside it
    // would be a second, competing answer to the same question.
    ...(payload.identity
      ? { identity: payload.identity }
      : isUUID(payload.medicationId)
        ? { medicationId: payload.medicationId }
        : { rawMedicationName: payload.name }),
    ...(payload.displayName !== undefined && {
      displayName: payload.displayName,
    }),
    ...(payload.medicationRole != null && {
      medicationRole: payload.medicationRole,
    }),
    ...(payload.form && {
      form: payload.form as PatientMedicationFormValue,
    }),
    ...(reminder && { reminder }),
  };
}
